"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import Image from "next/image";
import agenciesGraphic from "@/app/(public)/shared/assets/graphics/agencies.png";
import { CheckCircle } from "lucide-react";

export default function Agencies() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <main className="bg-landing-primary">
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">Your behind-the-scenes partner for seamless execution.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                From sourcing to contracting to day-of support, Nowadays works behind your brand to make you look like a
                hero.
              </p>
              <div className="flex gap-3">
                <Button variant="primary" color="primary" size="md" onClick={() => openModal("demo")}>
                  Get Started
                </Button>
              </div>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="enterprise-small"
              trustedByText="Trusted by world class companies"
              className="justify-start"
            />
          </div>
          <div className="flex items-center justify-center lg:h-full mt-8 lg:mt-0">
            <div className="w-full max-w-lg lg:max-w-none">
              <Image
                src={agenciesGraphic}
                alt="Agencies solution illustration"
                className="w-full h-auto object-contain rounded-2xl"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Document Style */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Client RFPs and white-label solutions that impress</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Centralize multiple client projects, present professional vendor comparisons, and maintain your brand
              presence with white-label solutions that deliver premium results.
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Centralize RFPs, quotes, and vendor timelines</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Streamline your workflow with our comprehensive platform that brings all your event planning needs into
                one place. Manage multiple client projects efficiently with centralized tools and real-time updates.
                Keep track of vendor responses, compare proposals, and maintain clear visibility across all active
                projects.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Compare options side-by-side for your clients</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Present clear, professional comparisons that help your clients make informed decisions quickly. Our
                side-by-side analysis tools make it easy to showcase value and justify recommendations. Transform
                complex vendor data into compelling presentations that demonstrate your expertise.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Custom white-label solutions available</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Maintain your brand presence with customizable solutions that integrate seamlessly with your existing
                processes. White-label options let you deliver our capabilities under your own brand, ensuring
                consistent client experiences while expanding your service offerings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section - Document Style */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Trusted partnerships that scale your business</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              We partner with agencies worldwide to deliver exceptional event experiences and expand your service
              capabilities.
            </p>
          </div>

          {/* Partnership Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Northstar Meetings Group</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                We proudly partner with{" "}
                <a
                  href="https://www.northstarmeetingsgroup.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 underline"
                >
                  Northstar Meetings Group
                </a>{" "}
                to help source venues for over 500+ client events through our white-label solution. This strategic
                partnership allows them to offer comprehensive venue sourcing while maintaining their brand identity and
                client relationships.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">American Global Business Travel</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Licensing our product for internal travel agents planning corporate events. Serving major clients
                including Blackrock through their client innovation council. Our enterprise licensing model provides
                them with the tools and infrastructure to deliver exceptional event planning services at scale.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Jamjar</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Partnered with this African event agency to build AI chatbot infrastructure, leveraging their historical
                quote data to enhance client interactions. This innovative collaboration demonstrates how our technology
                adapts to local markets while improving efficiency and client communication.
              </p>
            </div>
          </div>

          <div className="text-center mt-16">
            <Button variant="secondary" color="primary" size="md" onClick={() => openModal("demo")}>
              Explore Partnership Opportunities
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 sm:py-0">
        <div className="">
          <Testimonial variant="single" person="david-blansfield" title="Don't just take our word for it." />
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Get Started with Agencies"
        subtitle="Schedule a call to learn how we can support your agency"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your call with our team"
      />
    </main>
  );
}
